<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { Link } from 'svelte-routing';
  import { authFetch } from '../authService'; // <-- IMPORT THIS

  let stats = null;
  let isLoading = true;
  let error = '';
  let currentUser = null;

  authStore.subscribe(value => {
    currentUser = value.user;
  });

  onMount(async () => {
    if (currentUser) {
        try {
            const userId = currentUser.idTokenClaims.oid;
            // --- USE authFetch INSTEAD OF fetch ---
            // This automatically adds the Authorization: Bearer header
            const res = await authFetch(getApiUrl(`/dashboard/${userId}`));
            
            if (!res.ok) throw new Error("Failed to load dashboard.");
            stats = await res.json();
        } catch(e) {
            error = e.message;
            // If 401, it means token issue
            if (error.includes("401")) {
                error = "Session expired. Please login again.";
            }
        } finally {
            isLoading = false;
        }
    }
  });
  
  function formatCurrency(amount) {
      return `$${(amount || 0).toFixed(2)}`;
  }
</script>

<div class="dashboard-container">
    <h2>My Dashboard</h2>
    
    {#if isLoading}
        <p>Loading stats...</p>
    {:else if error}
        <p class="error">{error}</p>
    {:else if stats}
        
        <!-- NEW: Wallet & Marketplace Section -->
        <div class="section">
            <h3>💳 Wallet & Marketplace</h3>
            <div class="stats-grid">
                <div class="card highlight-credits">
                    <span class="label">My Credits</span>
                    <span class="value large-text">{stats.creditBalance || 0}</span>
                </div>
                <div class="card">
                    <span class="label">Items Bought</span>
                    <span class="value">{stats.marketPurchases || 0}</span>
                </div>
                <div class="card">
                    <span class="label">Items Sold</span>
                    <span class="value">{stats.marketSales || 0}</span>
                </div>
                <div class="card">
                    <span class="label">Total Listed</span>
                    <span class="value">{stats.marketPosted || 0}</span>
                </div>
            </div>
            <!-- Quick Actions for this section -->
             <div class="quick-actions">
                <Link to="/credits/topup" class="text-link">Buy Credits</Link>
                <Link to="/marketplace" class="text-link">Browse Market</Link>
                <Link to="/marketplace/sell" class="text-link">Sell Item</Link>
             </div>
        </div>

        <!-- 1. Savings Section -->
        <div class="section">
            <h3>💰 My Savings</h3>
            <div class="stats-grid">
                <div class="card">
                    <span class="label">Today</span>
                    <span class="value">{formatCurrency(stats.savingsToday)}</span>
                </div>
                <div class="card">
                    <span class="label">This Week</span>
                    <span class="value">{formatCurrency(stats.savingsWeek)}</span>
                </div>
                <div class="card">
                    <span class="label">This Month</span>
                    <span class="value">{formatCurrency(stats.savingsMonth)}</span>
                </div>
                <div class="card highlight">
                    <span class="label">All Time</span>
                    <span class="value">{formatCurrency(stats.savingsAllTime)}</span>
                </div>
            </div>
        </div>

        <!-- 2. Challenges Section (Pie Chart) -->
        <div class="section split-view">
            <div class="chart-card">
                <h3>🎯 Challenges</h3>
                <div class="chart-container">
                    <div class="pie-chart" style="
                        --p-completed: {(stats.challengesCompleted / (stats.challengesJoined || 1)) * 100}%;
                        --color-completed: #28a745;
                        --color-progress: #ffc107;
                    "></div>
                    <div class="legend">
                        <div class="legend-item"><span class="dot c-green"></span> Completed: {stats.challengesCompleted}</div>
                        <div class="legend-item"><span class="dot c-yellow"></span> In Progress: {stats.challengesInProgress}</div>
                        <div class="legend-item total"><strong>Total Joined: {stats.challengesJoined}</strong></div>
                    </div>
                </div>
                <div class="card-action">
                    <Link to={`/achievements/${currentUser.idTokenClaims.oid}`} class="text-link">→ Log a Transaction</Link>
                </div>
            </div>

            <!-- 3. Contributions Section -->
             <div class="contrib-card">
                <h3>🌟 Contributions</h3>
                <ul class="contrib-list">
                    <li>
                        <span>Promotions Posted</span>
                        <strong>{stats.promotionsPosted}</strong>
                    </li>
                    <li>
                        <span>Products Scanned</span>
                        <strong>{stats.productsPosted}</strong>
                    </li>
                    <li>
                        <span>Favourites Added</span>
                        <strong>{stats.favouritesCount}</strong>
                    </li>
                    <li>
                        <span>Products Liked</span>
                        <strong>{stats.likesCount}</strong>
                    </li>
                </ul>
                <div class="card-action-group">
                    <Link to="/scan" class="text-link">→ Scan Promotion</Link>
                    <Link to="/products/scan" class="text-link">→ Scan Product</Link>
                </div>
             </div>
        </div>

        <!-- 4. Shortcuts -->
        <div class="section shortcuts">
            <h3>🚀 Quick Actions</h3>
            <div class="btn-grid">
                <Link to={`/inbox/${currentUser.idTokenClaims.oid}`} class="shortcut-btn msg">
                    📩 My Inbox
                </Link>
                <Link to="/profile" class="shortcut-btn pf">
                    👤 My Profile
                </Link>
            </div>
        </div>

    {/if}
</div>

<style>
    .dashboard-container { max-width: 1000px; margin: 2rem auto; padding: 0 1rem; }
    h2 { margin-bottom: 2rem; text-align: center; color: #333; }
    h3 { margin-top: 0; font-size: 1.2rem; color: #555; border-bottom: 2px solid #eee; padding-bottom: 0.5rem; margin-bottom: 1rem; }
    
    .section { margin-bottom: 2.5rem; }
    
    /* Stats Grid */
    .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 1rem; }
    .card { background: white; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.05); text-align: center; }
    .card .label { display: block; font-size: 0.9rem; color: #777; margin-bottom: 0.5rem; }
    .card .value { font-size: 1.5rem; font-weight: bold; color: #333; }
    
    .card.highlight { background: #e6fffa; border: 1px solid #38b2ac; }
    .card.highlight .value { color: #234e52; }
    
    .card.highlight-credits { background: #fff8e1; border: 1px solid #ffc107; }
    .large-text { font-size: 2rem !important; color: #d39e00 !important; }

    .quick-actions { display: flex; justify-content: center; gap: 2rem; margin-top: 1rem; }

    /* Split View */
    .split-view { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
    @media(min-width: 768px) { .split-view { grid-template-columns: 1fr 1fr; } }
    
    .chart-card, .contrib-card { background: white; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.05); display: flex; flex-direction: column; }
    
    /* Pie Chart CSS */
    .chart-container { display: flex; align-items: center; gap: 2rem; justify-content: center; flex-grow: 1; }
    .pie-chart {
        width: 120px; height: 120px; border-radius: 50%;
        background: conic-gradient(
            var(--color-completed) 0% var(--p-completed), 
            var(--color-progress) var(--p-completed) 100%
        );
    }
    .legend-item { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.5rem; font-size: 0.9rem; }
    .dot { width: 10px; height: 10px; border-radius: 50%; }
    .c-green { background: #28a745; }
    .c-yellow { background: #ffc107; }
    .total { margin-top: 1rem; border-top: 1px solid #eee; padding-top: 0.5rem; }

    /* Contribution List */
    .contrib-list { list-style: none; padding: 0; margin: 0; flex-grow: 1; }
    .contrib-list li { display: flex; justify-content: space-between; padding: 0.8rem 0; border-bottom: 1px solid #f5f5f5; }
    .contrib-list li:last-child { border-bottom: none; }
    
    /* Action Links */
    .card-action { margin-top: 1.5rem; text-align: center; border-top: 1px solid #eee; padding-top: 1rem; }
    .card-action-group { margin-top: 1.5rem; text-align: center; border-top: 1px solid #eee; padding-top: 1rem; display: flex; justify-content: space-around; }
    .text-link { color: #007bff; text-decoration: none; font-weight: 500; font-size: 0.95rem; }
    .text-link:hover { text-decoration: underline; }

    /* Shortcuts */
    .btn-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; }
    .shortcut-btn { 
        display: block; text-align: center; padding: 1rem; text-decoration: none; 
        border-radius: 8px; font-weight: bold; color: white; transition: opacity 0.2s;
    }
    .shortcut-btn:hover { opacity: 0.9; }
    .msg { background-color: #007bff; }
    .pf { background-color: #6c757d; }
    .scan { background-color: #17a2b8; }

    .error { color: red; text-align: center; }
</style>